/*
  UserReport/ListComponent.js - Creates Table headers and populates body
*/

// NPM
import React, { useState, useEffect } from 'react';

// MUI COMPONENTS
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

// COMPONENTS
import DateRangeSelector from '../../common/DateRangeSelector';
import BackButton from '../../common/BackButton';

// NETWORKING
import useUserReportRequest from '../../../requests/UserReportRequest';

// FORMATTED MESSAGES
import UserReportsFormattedMessages from '../../../messages/reports/ReportsFormattedMessages';

const ReportDetail = (props) => {
  const { getUserReportDetails } = useUserReportRequest();
  const [data, setData] = useState({
    sent: [],
    received: [],
    pending: [],
    pending_sent: [],
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Initial data fetch can be added here if needed
  }, []);

  const handleUserDataSearch = async (fromDate, toDate) => {
    try {
      setData({
        sent: [],
        received: [],
        pending: [],
        pending_sent: [],
      });
      setIsLoading(false);

      let result = await getUserReportDetails(
        props.user.customer_user_id,
        fromDate,
        toDate,
        props.errorHandler
      );

      setData(result);
      setIsLoading(true);
    } catch (e) {
      // TODO - handle error
    }
  };

  const formatSentSmsRows = () => {
    return data.sent.map((item, index) => <SmsRow key={index} data={item} />);
  };

  const formatReceivedSmsRows = () => {
    return data.received.map((item, index) => (
      <SmsRow key={index} data={item} />
    ));
  };

  const formatReminderSentRows = () => {
    return data.pending_sent.map((item, index) => (
      <SentReminderRow key={index} data={item} />
    ));
  };

  const formatReminderPendingRows = () => {
    return data.pending.map((item, index) => (
      <PendingReminderRow key={index} data={item} />
    ));
  };

  // Generates the Lander code (back button and title)
  const generateLander = () => {
    return (
      <Box>
        <BackButton onClick={props.backButton} />

        <Typography variant="h3">
          {UserReportsFormattedMessages.reportUserDetail}
        </Typography>
      </Box>
    );
  };

  // Adds the Name of the current User whose SMS are being searched for
  const generateUserMessage = () => {
    let name = props.user ? props.user.user_name : '';
    return <Typography>User: {name}</Typography>;
  };

  /*
    Generate Timezone information
  */
  const generateTimezoneMessage = () => {
    let offset = new Date().getTimezoneOffset();
    offset = offset / 60; // Divide by 60 for a UTC style offset

    // Add + sign if positive number
    if (offset > 0) offset = `+${offset}`;

    return (
      <div>
        <Typography>Standard Timezone Offset is: (UTC +/-0)</Typography>
        <Typography>Detected Local Timezone: (UTC {offset})</Typography>
      </div>
    );
  };

  /*
    Generates the Search Tools for querying the data
  */
  const generateSearchTools = () => {
    return (
      <DateRangeSelector
        handleDateSearch={(fromDate, toDate) =>
          handleUserDataSearch(fromDate, toDate)
        }
      />
    );
  };

  // Generates the Results Table
  const generateTable = (title, htmlFunc) => {
    return (
      <TableContainer sx={{ marginBottom: 4, overflowX: 'auto' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell className="medium tableTop left">{title}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{htmlFunc()}</TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <div>
      {generateLander()}
      {generateUserMessage()}
      <br />
      {generateTimezoneMessage()}
      <br />
      {generateSearchTools()}
      <br />
      <br />
      {generateTable('Messages Sent', formatSentSmsRows)}
      <br />
      <br />
      {generateTable('Replies Received', formatReceivedSmsRows)}
      <br />
      <br />
      {generateTable('Reminders Sent', formatReminderSentRows)}
      <br />
      <br />
      {generateTable('Reminders Pending', formatReminderPendingRows)}
    </div>
  );
};

// Row Types
const SmsRow = ({ data }) => {
  const generateMessageRows = () => {
    let { messages } = data;
    return messages.map((msg, index) => {
      let rowText =
        `SMS:[${msg.sms_id}] Sent Server:[${msg.sent_server}] To:[${msg.to}] Reply Via:[${msg.reply_via}] SMS Type:[${msg.sms_type}] Quantity:[${msg.quantity}] Sent Flag:[${msg.sent_flag}]` +
        `DeliveryDevice:[${msg.delivery_device}] ] ReplyDevice:[${msg.reply_device}] ] Carrier:[${msg.carrier_id}, ${msg.carrier_name}] Country:[${msg.country_id}, ${msg.country_name}]`;
      return (
        <TableRow key={index}>
          <TableCell>{rowText}</TableCell>
        </TableRow>
      );
    });
  };

  let mainText = `EmailId:[${data.email_id}] SW Release:[${data.sw_release}] Received Server:[${data.received_server}]`; //Received Local:[01-Sep-20 09:46:17]
  return (
    <TableBody>
      <TableRow key={data.email_id}>
        <TableCell>{mainText}</TableCell>
      </TableRow>
      {generateMessageRows()}
    </TableBody>
  );
};

const SentReminderRow = ({ data }) => {
  const generateMessageRows = () => {
    let { messages } = data;
    return messages.map((msg, index) => {
      let rowText = `SMS:[${msg.sms_id}]Sent Server:[${msg.sent_server}]To:[${msg.to}]Sent Flag:[${msg.sent_flag}]`;
      return (
        <TableRow key={index}>
          <TableCell>{rowText}</TableCell>
        </TableRow>
      );
    });
  };

  let mainText = `Schedule${data.sched_id}Type:[${data.recurring}]SW Release:[${data.sw_release}]Received Server:[${data.created_server}]Due Server:[${data.due_server}]Status Flag:[${data.status_flag}]`;
  return (
    <TableBody>
      <TableRow key={data.sched_id}>
        <TableCell>{mainText}</TableCell>
      </TableRow>
      {generateMessageRows()}
    </TableBody>
  );
};

const PendingReminderRow = ({ data }) => {
  const generateMessageRows = () => {
    let { reminders } = data;
    return reminders.map((msg, index) => {
      let rowText = `Schedule:[${msg.sched_id}]Type:[${msg.recurring}]SW Release:[${msg.sw_release}]Received Server:[${msg.created_server}]Due Server:[${msg.due_server}]Status Flag:[${msg.status_flag}]`;
      return (
        <TableRow key={index}>
          <TableCell>{'>'}</TableCell>
          <TableCell>{rowText}</TableCell>
        </TableRow>
      );
    });
  };

  let mainText = `Schedule${data.sched_id}Type:[${data.recurring}]SW Release:[${data.sw_release}]Received Server:[${data.created_server}]Due Server:[${data.due_server}]Status Flag:[${data.status_flag}]`;
  return (
    <TableBody>
      <TableRow key={data.sched_id}>
        <TableCell>{mainText}</TableCell>
      </TableRow>
      {generateMessageRows()}
    </TableBody>
  );
};

export default ReportDetail;
